/**
 * /hospitality/gaming/floor — Gaming Floor win / hold by unit.
 *
 * Mirrors a casino's gaming-floor day: drop, actual vs theoretical win, and
 * actual vs theoretical hold for each gaming unit (slot bank, table pit, etc.),
 * with the hold anomalies surfaced as prominent flags at the top.
 *
 * Money fields arrive as decimal strings; `money()` formats them. The hold
 * fields (actual_hold, theoretical_hold) are already-formatted percent strings
 * and are rendered as-is.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import {
  PageHeader,
  StatCard,
  Panel,
  StatusPill,
  SeverityIcon,
  money,
} from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/gaming/floor")({
  component: GamingFloorPage,
});

// ── Types — matches GET /api/hospitality/gaming/floor contract ─────────────
interface GamingFloorUnit {
  code: string;
  name: string;
  kind: string;
  game_type: string;
  units_count: number;
  drop: string;
  actual_win: string;
  theoretical_win: string;
  actual_hold: string; // already formatted, e.g. "7.63%"
  theoretical_hold: string; // already formatted, e.g. "8.00%"
  variance: string;
  status: "ok" | "exception";
}
interface GamingFloorTotals {
  drop: string;
  actual_win: string;
  theoretical_win: string;
  variance: string;
  actual_hold: string; // already formatted percent string
}
interface GamingFloorFlag {
  unit: string;
  severity: "high" | "medium";
  title: string;
  detail: string;
}
interface GamingFloorResult {
  property: string;
  gaming_date: string | null;
  by_unit: GamingFloorUnit[];
  totals: GamingFloorTotals | null;
  flags: GamingFloorFlag[];
  message?: string;
}

function GamingFloorPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-gaming-floor"],
    queryFn: () => api<GamingFloorResult>("/api/hospitality/gaming/floor"),
  });

  const data = q.data;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Gaming Floor"
        subtitle={
          data
            ? `${data.property}${data.gaming_date ? ` · ${data.gaming_date}` : ""}`
            : "Win / hold by gaming unit"
        }
        right={
          data && !data.message && data.flags.length > 0 ? (
            <StatusPill tone="exception">
              {data.flags.length} flag{data.flags.length === 1 ? "" : "s"}
            </StatusPill>
          ) : undefined
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
            <br />
            <span className="text-xs">
              Gaming-floor results populate once unit win is imported for a gaming date.
            </span>
          </div>
        )}

        {!q.isLoading && data && !data.message && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard label="Total win" value={money(data.totals?.actual_win ?? null, 0)} />
              <StatCard
                label="Theoretical"
                value={money(data.totals?.theoretical_win ?? null, 0)}
              />
              <StatCard label="Hold %" value={data.totals?.actual_hold ?? "—"} />
              <StatCard
                label="Variance"
                value={money(data.totals?.variance ?? null, 0)}
                tone={data.totals?.variance?.startsWith("-") ? "danger" : "default"}
              />
            </div>

            {/* Hold-anomaly flags — prominent */}
            {data.flags.length > 0 && (
              <Panel label="Hold anomalies">
                <ul>
                  {data.flags.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 py-2 border-t border-slate-100 dark:border-slate-800 first:border-t-0"
                    >
                      <SeverityIcon severity={f.severity} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {f.title}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {f.detail}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </Panel>
            )}

            {/* Win / hold by unit */}
            <Panel label="Win / hold by unit">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Unit</th>
                    <th className="text-left py-1.5 px-3">Type</th>
                    <th className="text-right py-1.5 px-3">Drop</th>
                    <th className="text-right py-1.5 px-3">Actual win</th>
                    <th className="text-right py-1.5 px-3">Theo win</th>
                    <th className="text-right py-1.5 px-3">Hold</th>
                    <th className="text-right py-1.5 px-3">Theo hold</th>
                    <th className="text-right py-1.5 px-3">Variance</th>
                    <th className="text-right py-1.5 pl-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_unit.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="py-12 text-center text-slate-500 dark:text-slate-400"
                      >
                        No gaming-unit activity for this gaming date.
                      </td>
                    </tr>
                  )}
                  {data.by_unit.map((u) => (
                    <tr key={u.code} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3">
                        <div className="text-slate-800 dark:text-slate-200">{u.name}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{u.code}</div>
                      </td>
                      <td className="py-1.5 px-3 text-slate-600 dark:text-slate-300">
                        {u.game_type}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs">
                        {money(u.drop)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(u.actual_win)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {money(u.theoretical_win)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {u.actual_hold}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {u.theoretical_hold}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs">
                        {money(u.variance)}
                      </td>
                      <td className="py-1.5 pl-3 text-right">
                        {u.status === "exception" ? (
                          <StatusPill tone="exception">review</StatusPill>
                        ) : (
                          <StatusPill tone="success">ok</StatusPill>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}
